import { useEffect, useMemo, useState } from 'react';
import { getCountryFlagImage } from 'utils/helper-functions/getCountryFlagImage';
import type {
  CountrySelectionItem,
  CountrySelectionLangBack,
  CountrySelectionProps,
  CountrySelectionSource,
} from './countrySelectionTypes';

const toItem = (code: string, name: string, countryCode: string | null): CountrySelectionItem => ({
  code,
  name,
  countryCode,
  flagSrc: countryCode ? getCountryFlagImage(countryCode) || null : null,
});

export const normalizeCountrySelectionItems = (
  source: CountrySelectionSource,
): CountrySelectionItem[] => {
  if (source.mode === 'regionCode') {
    return source.dialCodes
      .map(({ dialCode, name, countryCode }) => toItem(dialCode, name, countryCode || null))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return Object.entries(source.countriesList).map(([code, name]) => toItem(code, name, code));
};

export const filterCountrySelectionItems = (
  items: CountrySelectionItem[],
  searchQuery: string,
): CountrySelectionItem[] => {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return items;

  const compactQuery = query.replace(/[\s+]/g, '');

  return items.filter(({ code, name }) => {
    const lowerCode = code.toLowerCase();
    const matchesCode = lowerCode.includes(query);
    const matchesName = name.toLowerCase().includes(query);
    const matchesPlus = `+${lowerCode}`.includes(query);
    const matchesCompactCode =
      Boolean(compactQuery) && lowerCode.replace(/\s/g, '').includes(compactQuery);
    return matchesCode || matchesName || matchesPlus || matchesCompactCode;
  });
};

export const getCountrySelectionTexts = (
  langBack: CountrySelectionLangBack,
  isRegionCode: boolean,
) => ({
  title: isRegionCode
    ? langBack('regionCode') || langBack('Select region code') || 'Select region code'
    : langBack('country') || langBack('Select country') || 'Select country',
  searchPlaceholder: isRegionCode
    ? langBack('searchPhoneCode') || 'Search phone code'
    : langBack('searchCountry') || 'Search country',
  emptyTitle: isRegionCode
    ? langBack('noRegionFound') || 'No region found'
    : langBack('noCountryFound') || 'No country found',
  emptySubtitle: isRegionCode
    ? langBack('noRegionFoundSubtitle') ||
      "We couldn't find a region or code matching your search, try again"
    : langBack('noCountryFoundSubtitle') ||
      "We couldn't find a country matching your search, try again",
});

/**
 * Shared state for the country / dial-code pickers (bottom sheet and desktop dropdown):
 * normalises both modes to one item shape, filters on the search query and emits the mode callback.
 */
export const useCountrySelectionItems = (props: CountrySelectionProps) => {
  const { isOpen, langBack, selectedValue, hideFlagsInRegionCode = false } = props;
  const isRegionCode = props.mode === 'regionCode';
  const dialCodes = props.mode === 'regionCode' ? props.dialCodes : undefined;
  const countriesList = props.mode === 'regionCode' ? undefined : props.countriesList;
  const selectedCountryCode =
    props.mode === 'regionCode' ? props.selectedCountryCode || null : null;

  const [searchQuery, setSearchQuery] = useState('');

  const items = useMemo(
    () =>
      isRegionCode
        ? normalizeCountrySelectionItems({ mode: 'regionCode', dialCodes: dialCodes || [] })
        : normalizeCountrySelectionItems({ mode: 'country', countriesList: countriesList || {} }),
    [isRegionCode, dialCodes, countriesList],
  );

  const filteredItems = useMemo(
    () => filterCountrySelectionItems(items, searchQuery),
    [items, searchQuery],
  );

  const isItemSelected = (item: CountrySelectionItem) =>
    selectedValue === item.code &&
    (!selectedCountryCode || item.countryCode === selectedCountryCode);

  const selectItem = (item: CountrySelectionItem) => {
    if (props.mode === 'regionCode') {
      props.onDialCodeChange(item.code, item.countryCode);
    } else {
      props.onCountryChange(item.code);
    }
  };

  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  return {
    isRegionCode,
    showFlags: !(isRegionCode && hideFlagsInRegionCode),
    searchQuery,
    setSearchQuery,
    clearSearch: () => setSearchQuery(''),
    items,
    filteredItems,
    isItemSelected,
    selectItem,
    texts: getCountrySelectionTexts(langBack, isRegionCode),
  };
};
